import React, { useState, useRef, useEffect } from 'react';
import DashboardInsights from '../pages/DashboardInsights';

const Dashboard = ({ dailyHrvData, weeklyHrvData, monthlyHrvData, quarterlyHrvData, maxDateVal, submitUpdatedTemplate }) => {

    const [hrvBreakdownData, setHrvBreakdownData] = useState(weeklyHrvData);
    const [selectedPeriod, setSelectedPeriod] = useState('weekly-score');
    const [scorePeriod, setScorePeriod] = useState('7');
    const [showLowestHRTooltip, setShowLowestHRTooltip] = useState(false);
    const [showHighestHRTooltip, setShowHighestHRTooltip] = useState(false);
    const [showLowestHRVTooltip, setShowLowestHRVTooltip] = useState(false);
    const [showHighestHRVTooltip, setShowHighestHRVTooltip] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const modalRef = useRef(null);
    const [maxPeriodVal, setMaxPeriodVal] = useState(weeklyHrvData.metrics.hrv.max.value);
    const [maxPeriodDate, setMaxPeriodDate] = useState(weeklyHrvData.metrics.hrv.max.date);
    const [showOptimizationProtocols, setShowOptimizationProtocols] = useState(false);
    const getTemplateNumber = (period) => {
        const HRV = dailyHrvData.metrics.hrv.mean;
        const HR = dailyHrvData.metrics.heart_rate.mean;
        let dashboardData;
        if(period === 'weekly') {
            dashboardData = weeklyHrvData;
        } else if(period === 'monthly') {
            dashboardData = monthlyHrvData;
        } else if(period === 'quarterly') {
            dashboardData = quarterlyHrvData;
        }
        const hrv_p75 = dashboardData.metrics.hrv.p75;
        const hrv_p25 = dashboardData.metrics.hrv.p25;
        const heart_rate_p75 = dashboardData.metrics.heart_rate.p75;
        const heart_rate_p25 = dashboardData.metrics.heart_rate.p25;

        let heart_rate_level, hrv_level, hrv_variability_level;

        if(HR >= heart_rate_p75) {
            heart_rate_level = 'high';
        } else if(HR <= heart_rate_p25) {
            heart_rate_level = 'low';
        } else {
            heart_rate_level = 'medium';
        }

        if(HRV >= hrv_p75) {
            hrv_level = 'high';
        } else if(HRV <= hrv_p25) {
            hrv_level = 'low';
        } else {
            hrv_level = 'medium';
        }

        if(dashboardData.metrics.hrv.mean >= 10) {
            hrv_variability_level = 'high';
        } else if(dashboardData.metrics.hrv.mean <= 4) {
            hrv_variability_level = 'low';
        } else {
            hrv_variability_level = 'medium';
        }

        if(hrv_level === "low") {
            if(heart_rate_level === "high") {
                return 6;
            }
            return 5;
        } else if(hrv_level === "medium") {
            if(heart_rate_level === "high") {
                return 4;
            }
            return 3;
        } else {
            if(heart_rate_level === "low" && hrv_variability_level === "high") {
                return 0;
            } else if(heart_rate_level === "high") {
                return 2;
            } else if(heart_rate_level === "medium") {
                if(hrv_variability_level === "high") {
                    return 2;
                } else {
                    return 1;
                }
            } else {
                if(hrv_variability_level === "high") {
                    return 0;
                } else {
                    return 1;
                }
            }
        }
    }
    const tempNum = getTemplateNumber('weekly');
    const [selectedOption, setSelectedOption] = useState((tempNum+1).toString());
    const [originalTemplateNumber, setOriginalTemplateNumber] = useState(tempNum);
    const [templateNumber, setTemplateNumber] = useState(weeklyHrvData.template_number);
    const [analysisKeyInsights, setAnalysisKeyInsights] = useState();

    const calculateRange = (dailyData, rangeData, placement) => {
        let min = rangeData.mean - rangeData.variability;
        let max = rangeData.mean + rangeData.variability;
        if (placement === "dashboard-insights-hr") {
            min = 66;
            max = 70;
        }
        let range = '';
        let rangeComp = '';
        if (dailyData >= min.toFixed(0) && dailyData <= max.toFixed(0)) {
            range = 'In Range';
            rangeComp = 'within';
        } else if (dailyData < min.toFixed(0)) {
            range = 'Below Range';
            rangeComp = 'below';
        } else {
            range = 'Above Range';
            rangeComp = 'above';
        }
        if (placement === "dashboard") {
            return (
                <>
                    <div>{range}</div>
                    <div>({min.toFixed(0)} - {max.toFixed(0)})</div>
                </>
            );
        } else if (placement === "dashboard-insights-hr") {
            return {
                rangeComparison: rangeComp
            };
        }
        return {
            rangeComparison: rangeComp,
            range: `(${min.toFixed(0)} - ${max.toFixed(0)})`
        };
    }
    
    const highHRVMessage = {
        "heading": "Big swings in HRV (Heart Rate Variability) can indicate autonomic instability. These fluctuations may be due to:",
        "values": [
            "Blood Volume Changes – Dehydration or electrolyte imbalances can cause HRV to swing.",
            "Sympathetic Overactivation – Stress, fatigue, or medication changes can cause shifts in autonomic tone.",
            "Vagal Nerve Stimulation – If you are using PEMF on the vagus nerve, it may temporarily influence HRV.",
            "Mestinon Effects – Since Mestinon affects the autonomic nervous system, your dose timing might impact HRV swings.",
            "Sleep Quality – Poor sleep or nighttime wakings (like your internal tremors) can cause HRV to fluctuate.",
        ]
    };

    const handleSelect = (button) => {
        if (button === 'weekly-score') {
            setSelectedPeriod(button);
            setScorePeriod('7');
            const tempNum = getTemplateNumber('weekly');
            setSelectedOption((tempNum+1).toString());
            setTemplateNumber(weeklyHrvData.template_number);
            setHrvBreakdownData(weeklyHrvData);
            setMaxPeriodVal(weeklyHrvData.metrics.hrv.max.value);
            setMaxPeriodDate(weeklyHrvData.metrics.hrv.max.date);
            if(weeklyHrvData.hrv_range_status === "High variability") {
                setAnalysisKeyInsights([...analysis[weeklyHrvData.template_number].key_insights, highHRVMessage]);
            } else {
                setAnalysisKeyInsights(analysis[weeklyHrvData.template_number].key_insights);
            }
        } else if (button === 'monthly-score') {
            setSelectedPeriod(button);
            setScorePeriod('30');
            const tempNum = getTemplateNumber('monthly');
            setSelectedOption((tempNum+1).toString());
            setTemplateNumber(monthlyHrvData.template_number);
            setHrvBreakdownData(monthlyHrvData);
            setMaxPeriodVal(monthlyHrvData.metrics.hrv.max.value);
            setMaxPeriodDate(monthlyHrvData.metrics.hrv.max.date);
            if(monthlyHrvData.hrv_range_status === "High variability") {
                setAnalysisKeyInsights([...analysis[monthlyHrvData.template_number].key_insights, highHRVMessage]);
            } else {
                setAnalysisKeyInsights(analysis[monthlyHrvData.template_number].key_insights);
            }
        } else if (button === 'quarterly-score') {
            setSelectedPeriod(button);
            setScorePeriod('90');
            const tempNum = getTemplateNumber('quarterly');
            setSelectedOption((tempNum+1).toString());
            setTemplateNumber(quarterlyHrvData.template_number);
            setHrvBreakdownData(quarterlyHrvData);
            setMaxPeriodVal(quarterlyHrvData.metrics.hrv.max.value);
            setMaxPeriodDate(quarterlyHrvData.metrics.hrv.max.date);
            if(quarterlyHrvData.hrv_range_status === "High variability") {
                setAnalysisKeyInsights([...analysis[quarterlyHrvData.template_number].key_insights, highHRVMessage]);
            } else {
                setAnalysisKeyInsights(analysis[quarterlyHrvData.template_number].key_insights);
            }
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent body scroll
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setShowOptimizationProtocols(false);
        setActiveTab(0);
        document.body.style.overflow = 'auto'; // Restore body scroll
    };

    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            closeModal();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const closeTooltip = () => {
        setShowLowestHRTooltip(false);
        setShowHighestHRTooltip(false);
        setShowLowestHRVTooltip(false);
        setShowHighestHRVTooltip(false);
    };

    useEffect(() => {
        document.addEventListener('mousedown', closeTooltip);
        return () => {
            document.removeEventListener('mousedown', closeTooltip);
        };
    }, []);

    function formatDateString(dateString) {
        const newDateString = addOneDayToDateString(dateString); 
        const [year, month, day] = newDateString.split("-").map(Number);
    
        // Array of month names (short format)
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
        return `${day} ${monthNames[month - 1]}, ${year}`;
    }

    function addOneDayToDateString(dateString) {
        let [year, month, day] = dateString.split("-").map(Number);
    
        // Days in each month (considering leap years for February)
        const daysInMonth = [31, (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28, 
                             31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    
        day += 1; // Add 1 day
    
        // If the new day exceeds the number of days in the month, move to the next month
        if (day > daysInMonth[month - 1]) {
            day = 1; // Reset to the first day of the next month
            month += 1; // Move to the next month
    
            if (month > 12) {
                month = 1; // Reset to January
                year += 1; // Move to the next year
            }
        }
    
        // Format back to "YYYY-MM-DD"
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }

    const performanceIndicators = ["Energy Levels", "Stress Resilience", "Recovery Capacity", "Emotional & Cognitive Performace"];

    const analysis =
    [
        {
            "protocol_number": "protocol_1",
            "key_insights": [
                {
                    "heading": "HRV Stability",
                    "values": [
                        "Nocturnal HRV is high, consistently within the optimal range, indicating a well-regulated autonomic nervous system and a state of internal abundance.",
                        "{period} HRV variability (e.g., {val} ms) reflects healthy flexibility, though optimizing this range could unlock even greater consistency in performance.",
                        "Your best {period} HRV score is {max_period_val} ms on {max_period_date}. Our AI recommends you to make note of the behaviours that contributed to this score and try to replicate them in the future."
                    ]
                },
                {
                    "heading": "Heart Rate Patterns",
                    "values": [
                        "Heart rate shows remarkable stability, with minimal fluctuation (e.g., {val} bpm).",
                        "This indicates elite cardiovascular regulation, often seen in high-performing individuals with advanced autonomic balance."
                    ]
                },
                {
                    "heading": "Performance Indicators",
                    "values": [
                        "Consistent energy levels throughout the day.",
                        "Strong resilience under stress, allowing for effective handling of daily challenges.",
                        "Possesses an ability to recover quickly.",
                        "Excellent cognitive performance and focus for decision-making. Stable emotional regulation, allowing for adaptability to life’s demands."
                    ]
                }
            ],
            "goals": [
                {
                    "heading": "Elevate HRV Potential",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Integrate dynamic biofeedback training (15 minutes twice per day) to target higher HRV ceilings during rest and lower HRV floors during activity.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Utilize resonance breathing (4-second inhale, 6-second exhale) combined with Dr. Lagos’ heart visualizations to enhance parasympathetic activation.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Streamline HRV Variability",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Introduce precision recovery windows throughout the day. These are 3-5 minute intervals where you step back from tasks to perform deep breathing, recalibrating your nervous system.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Set three recovery alarms during high-demand hours to implement this strategy.  Work towards being able to achieve resonance in three or less breaths.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Supercharge Morning Activation Protocols",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Use cold exposure techniques (e.g., cold splash on the face or a brief cold shower) to prime autonomic flexibility early in the day.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Pair cold exposure with light mobility exercises and 10 minutes of daylight exposure to reset your circadian rhythm and optimize alertness.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Optimize Daily Rhythms",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Build a customized circadian rhythm alignment schedule that syncs peak activity with your natural energy levels.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Use HRV data to map out peak and low energy periods, during your day, adjusting your most demanding tasks to coincide with your physiological highs.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Enhance Recovery Protocols",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Incorporate progressive sleep wind-down protocols.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Gradually dim lights every 15 minutes, starting 1 hour before bed.",
                                "Use a warm bath or sauna to accelerate thermoregulation.",
                                "Complete 5 minutes of gratitude journaling or reflection to reduce mental arousal.",
                                "Engage in 15 minutes of resonance breathing prior to sleep to calm the mind and body."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Integrate Technology for Optimization",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Use wearable technology to track micro-adjustments in HRV throughout the day.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Identify weekly HRV amplifiers and decelerators to understand how to achieve elite levels of physiological calibration for performance moments.",
                            "points": []
                        }
                    ]
                }
            ],
            "takeaway": "Your metrics showcase a resilient and adaptive autonomic nervous system, providing a robust foundation for high performance and efficient recovery. We can unlock even greater levels of balance, energy, and resilience by elevating your HRV potential, stabilizing variability between days, understanding what helps to amplify your HRV subtracting behaviors that lower it, as well as aligning daily rhythms with physiological patterns.",
            "next_steps": [
                "📈 Elevate HRV Strategies",
                "⚡ Identifying HRV Amplifiers and Detractors",
                "🌅 Morning Optimization Plans"
            ]
        },
        {
            "protocol_number": "protocol_2",
            "key_insights": [
                {
                    "heading": "HRV Stability",
                    "values": [
                        "Your nocturnal HRV sits solidly within the optimal range, showcasing strong physiological resilience.",
                        "{period} HRV variability (~{val} ms) reflects adequate flexibility, indicating stable and effective autonomic regulation.",
                        "Your best {period} HRV score is {max_period_val} ms on {max_period_date}. Our AI recommends you to make note of the behaviours that contributed to this score and try to replicate them in the future."
                    ]
                },
                {
                    "heading": "Heart Rate Patterns",
                    "values": [
                        "Heart rate shows minimal to moderate fluctuations (2–5 bpm), highlighting balanced cardiovascular and autonomic control.",
                        "This level of stability supports consistent recovery and stress regulation."
                    ]
                },
                {
                    "heading": "Performance Indicators",
                    "values": [
                        "Stable energy levels with good adaptability.",
                        "Enhanced stress resilience, allowing for effective handling of daily challenges.",
                        "Strong recovery capacity after physical and mental demands.",
                        "Balanced emotional and cognitive performance, fostering focus and composure."
                    ]
                }
            ],
            "goals": [
                {
                    "heading": "Consistency Goals",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Maintain HRV within the 15-18 ms range, refining autonomic balance.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Monitor HR trends daily and aim to reduce variability in heart rate to below 5 bpm for optimal stability.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "HRV Optimization",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Engage in regular aerobic activity (e.g., 20 minutes of walking or cycling each AM) to support energy regulation.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Incorporate resonance breathing for 15-20 minutes following exercise to enhance vagal control that impacts speech, social connectedness, cognitive dexterity, and self-regulation.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Stress Management",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Aim to consistently achieve your current weekly HRV deviations of 2-5 ms to maintain resilience even during times of challenge or change.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Schedule light recovery routines, such as stretching or yoga, throughout the day if you notice your heart rate jumping 20+ bpm while sitting still from your baseline heart rate.",
                            "points": []
                        }
                    ]
                }
            ],
            "takeaway": "Your metrics reflect a well-balanced autonomic system with solid resilience and adaptability. By maintaining your HRV and HR stability while refining weekly variability, you can enhance both recovery and performance potential.",
            "next_steps": [
                "📈 HRV Consistency Tips",
                "⚡ HRV Optimization Techniques",
                "🧘‍♂️ Stress Recovery Routines"
            ]
        },
        {
            "protocol_number": "protocol_3",
            "key_insights": [
                {
                    "heading": "HRV Stability",
                    "values": [
                        "Nocturnal HRV sits in the high range, indicating resilience but elevated variability suggests the autonomic system may be adapting to stress or strain.",
                        "Your best {period} HRV score is {max_period_val} ms on {max_period_date}. Our AI recommends you to make note of the behaviours that contributed to this score and try to replicate them in the future."
                    ]
                },
                {
                    "heading": "Heart Rate Patterns",
                    "values": [
                        "Moderate to significant fluctuations (6–12 bpm) indicate dynamic autonomic activity, highlighting transient stress or recovery challenges that could benefit from fine-tuning."
                    ]
                },
                {
                    "heading": "Performance Indicators",
                    "values": [
                        "Generally strong energy levels, though occasional variability is noted.",
                        "Slightly reduced stress resilience due to autonomic inconsistency.",
                        "Moderate recovery capacity with potential for improvement.",
                        "Balanced under most conditions but sensitive to autonomic shifts."
                    ]
                }
            ],
            "goals": [
                {
                    "heading": "HRV Alignment and Peak Performance Protocols",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Introduce dual-mode resonance breathing, alternating between low-frequency (4s inhale, 6s exhale) and high-frequency (3s inhale, 3s exhale) to train adaptability across autonomic states.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Perform dual-mode breathing for 10 minutes twice daily.",
                                "Use HRV biofeedback tools to monitor improvements in variability and identify optimal breathing patterns for specific situations."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Smooth HR Trends with Precision Recovery Cycles",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Integrate micro-recovery bursts—90-second recovery exercises like resonance breathing or progressive muscle relaxation—every 3 hours during high-demand days.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Use wearable devices to identify HR spikes above 10 bpm and initiate recovery bursts to stabilize autonomic activity.",
                                "Apply these bursts proactively during anticipated stress events to prevent significant HR variability."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Recovery Focus with Targeted Sleep Architecture Enhancement",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Employ neuro-priming techniques, such as binaural beats or alpha-wave music, 20 minutes before bed to promote deeper sleep phases.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Pair neuro-priming with a consistent pre-sleep routine involving resonance breathing and gratitude journaling. ",
                                "Use wearable technology to track deep sleep percentage and correlate changes with HRV trends."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Energy Optimization Through Active Recovery Intervals",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Introduce active recovery intervals—15-minute low-intensity exercises (e.g., walking, light cycling) paired with deep diaphragmatic breathing—immediately following high-demand tasks.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                            "Schedule active recovery after physical or mental exertion to support HR stabilization. ",
                            "Track HR recovery rate within 5 minutes post-interval to measure autonomic efficiency improvements."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Stress Resilience with Emotional Regulation Training",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Practice heart-focused emotional regulation training by combining visualization of positive emotions (e.g., gratitude, joy) with guided breathing to reduce physiological stress responses.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Dedicate 5 minutes twice daily to visualize positive experiences while maintaining a slow and controlled breathing pattern. ",
                                "Track HRV changes during these sessions to assess improvements in emotional regulation."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Advanced HRV-Driven Scheduling for Consistency",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Align your daily schedule with HRV-driven timing, using peak HRV periods for high-demand cognitive tasks and lower HRV times for recovery-focused activities.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Use HRV data from wearable devices to identify patterns in autonomic efficiency. ",
                                "Adjust workflows to maximize productivity during optimal autonomic states."
                            ]
                        }
                    ]
                }
            ],
            "takeaway": "Your metrics reveal a highly adaptable autonomic system that can benefit from advanced fine-tuning. By introducing innovative HRV training protocols, targeted recovery strategies, and HR stabilization techniques, you can unlock new levels of resilience, balance, and elite performance.",
            "next_steps": [
                "📈 Dual-Mode HRV Breathing Plans",
                "⚡ Precision Recovery Cycles",
                "🛌 Sleep Architecture Enhancements",
                "🧘‍♀️ Emotional Regulation Training"
            ]
        },
        {
            "protocol_number": "protocol_4",
            "key_insights": [
                {
                    "heading": "HRV Stability",
                    "values": [
                        "Your nocturnal HRV is in a moderate range, reflecting adequate resilience but with room to elevate recovery potential.",
                        "{period} HRV variability (~{val} ms) shows balanced adaptability, though refining consistency could further enhance stability.",
                        "Your best {period} HRV score is {max_period_val} ms on {max_period_date}. Our AI recommends you to make note of the behaviours that contributed to this score and try to replicate them in the future."
                    ]
                },
                {
                    "heading": "Heart Rate Patterns",
                    "values": [
                        "Heart rate fluctuations (3–6 bpm) suggest stable autonomic control, with occasional variations that could benefit from additional stabilization."
                    ]
                },
                {
                    "heading": "Performance Indicators",
                    "values": [
                        "Generally stable energy levels, with slight variability.",
                        "Moderate stress resilience, though occasionally strained by autonomic shifts.",
                        "Adequate recovery capacity but with room for improvement.",
                        "Reliable emotional and cognitive performance under most conditions."
                    ]
                }
            ],
            "goals": [
                {
                    "heading": "Heart-Focused Emotional Regulation Training",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Develop emotional coherence through guided visualization and resonance breathing, aligning HRV with positive emotional states.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Dedicate 10 minutes daily to heart-focused breathing, visualizing gratitude or positive outcomes, and track improvements in HRV during these sessions.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Refine Daily Patterns",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Implement adaptive task sequencing by aligning demanding activities with periods of peak HRV for optimal performance and recovery.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Use wearable data to schedule high-focus tasks during HRV peaks and reserve restorative activities for HRV dips.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Consistency in Recovery",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Use dynamic recovery tracking to reduce HRV variability to 2-3 ms.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Review HR trends daily and incorporate recovery practices (e.g., progressive muscle relaxation or short meditative breaks) during high-variability periods.",
                                "Enhance wind-down routines with gratitude journaling and dim light exposure."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Enhance Sleep Depth",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Combine targeted supplementation and temperature-regulating sleep technology to optimize restorative sleep.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Consider supplements such as the Lyma Sleep Formula or Huberman's recommended cocktail (magnesium threonate, apigenin, and theanine) for sleep depth.",
                                "Invest in an Eight Sleep Mattress for precise temperature control and enhanced sleep architecture.",
                                "Pair this with soundscapes or binaural beats to promote deeper sleep stages."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Boost Resilience with Multi-Sensory Relaxation",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Integrate multi-sensory relaxation protocols, using light, sound, and temperature to downregulate stress and improve autonomic function.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Incorporate a warm shower followed by resonance breathing with dim lighting and calming soundscapes to create a comprehensive recovery environment.",
                            "points": []
                        }
                    ]
                }
            ],
            "takeaway": "Your metrics reflect a stable and moderately adaptable system with significant potential for improvement in HRV and HR stability. By adopting strategies like heart-focused emotional regulation, adaptive task sequencing, targeted sleep optimization with supplements and temperature control, and multi-sensory relaxation protocols, you can achieve greater balance, recovery capacity, and resilience tailored to your needs.",
            "next_steps": [
                "💖 Heart-Focused Regulation Training",
                "📈 Adaptive Task Sequencing",
                "🛌 Sleep Optimization with Lyma and Eight Sleep"
            ]
        },
        {
            "protocol_number": "protocol_5",
            "key_insights": [
                {
                    "heading": "HRV Stability",
                    "values": [
                        "Your HRV is in the medium range, reflecting moderate resilience, but high variability suggests your autonomic nervous system is under strain.",
                        "Elevated HRV variability indicates inconsistencies in recovery, which can impact energy levels, emotional regulation, and cognitive sharpness.",
                        "Your best {period} HRV score is {max_period_val} ms on {max_period_date}. Our AI recommends you to make note of the behaviours that contributed to this score and try to replicate them in the future."
                    ]
                },
                {
                    "heading": "Heart Rate Patterns",
                    "values": [
                        "A high resting heart rate paired with significant variability (>10 bpm) indicates persistent sympathetic activation, which can reduce recovery efficiency and impair focus during high-demand periods.",
                        "These fluctuations suggest your system may be over-responding to daily stressors, making it difficult to sustain balance."
                    ]
                },
                {
                    "heading": "Performance Indicators",
                    "values": [
                        "Fluctuating, with noticeable dips during stress-heavy periods.",
                        "Moderate but reactive—heightened reactivity may slow recovery.",
                        "Inconsistent, leading to potential fatigue and reduced endurance.",
                        "Reliable under low stress but prone to lapses during periods of high variability."
                    ]
                }
            ],
            "goals": [
                {
                    "heading": "Stabilize HRV and Lower Resting Heart Rate Through Morning Routine",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Starting the day with intentional breathing and movement can reduce overnight autonomic activation, stabilize HRV, and lower your resting heart rate.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Begin with 10 minutes of resonance breathing (4-second inhale, 6-second exhale) to balance HRV and calm your system.",
                                "Follow with 15-20 minutes of moderate exercise, such as brisk walking or light cycling, to reduce sympathetic activation and improve cardiovascular health.",
                                "Include natural sunlight exposure during your routine to align your circadian rhythm, promoting better energy regulation and focus throughout the day."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Deepen Recovery With Optimized Sleep",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Sleep is vital for HRV and heart rate recovery. Enhancing your environment and pre-sleep routine will maximize restorative benefits.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Use supplements like magnesium threonate, theanine, or apigenin to support sleep depth and quality.",
                                "Optimize your sleep environment with temperature regulation (67-69°F) using an Eight Sleep Mattress or similar tools.",
                                "Dedicate 10 minutes to pre-sleep breathing or calming soundscapes to lower your heart rate and prepare for deep sleep."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Enhance Emotional Regulation for Cognitive Focus",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Emotional regulation is crucial for maintaining decision-making and resilience under stress. Combining visualization with breathing will help align your physiology with positive emotional states.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Before challenging tasks, spend 5-10 minutes visualizing calm, successful outcomes while practicing resonance breathing. Use this method to manage stress reactivity and sustain cognitive clarity during demanding situations.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Dynamic Recovery for High Variability Days",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "High variability days call for proactive recovery strategies to recalibrate your autonomic system.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Alternate 5 minutes of light movement (e.g., stretching or walking) with 5 minutes of resonance breathing to reset balance.",
                                "Use wearable devices to monitor your trends and adapt your recovery routine as needed."
                            ]
                        }
                    ]
                }
            ],
            "takeaway": "Your HRV and heart rate patterns show a strong foundation but suggest a need for greater balance and consistency. By integrating an intentional morning routine, optimizing recovery through sleep, and practicing emotional regulation, you can significantly enhance your energy, decision-making, and overall performance.",
            "next_steps": [
                "💖 Morning Routine for Stability and Recovery",
                "📈 Sleep Optimization for Deep Recovery",
                "💡 Emotional Regulation and Stress Management"
            ]
        },
        {
            "protocol_number": "protocol_6",
            "key_insights": [
                {
                    "heading": "HRV Stability",
                    "values": [
                        "Your nocturnal HRV is in a low range, reflecting reduced resilience and recovery capacity.",
                        "Insufficient HRV variability indicates diminished autonomic flexibility, which may impact stress adaptation and cognitive sharpness.",
                        "Your best {period} HRV score is {max_period_val} ms on {max_period_date}. Our AI recommends you to make note of the behaviours that contributed to this score and try to replicate them in the future."
                    ]
                },
                {
                    "heading": "Heart Rate Patterns",
                    "values": [
                        "Heart rate fluctuations (4–8 bpm) suggest stable but underperforming autonomic control.",
                        "This could lead to slower recovery and reduced focus during decision-heavy moments."
                    ]
                },
                {
                    "heading": "Performance Indicators",
                    "values": [
                        "Inconsistent energy levels, with noticeable dips during demanding periods.",
                        "Reduced stress resilience, making it harder to maintain composure under pressure.",
                        "Limited recovery capacity, slowing recovery after physical or mental exertion.",
                        "Prone to lapses in emotional and cognitive performance under pressure due to autonomic strain and reduced recovery efficiency."
                    ]
                }
            ],
            "goals": [
                {
                    "heading": "Morning Routine for Resilience and Focus",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Combine resonance breathing with moderate exercise to activate your autonomic system and boost mental clarity.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Begin with 15 minutes of resonance breathing (4-second inhale, 6-second exhale) to stabilize HRV and prime your nervous system.",
                                "Follow with 15 minutes of moderate exercise, such as a brisk walk or light cardio, to enhance blood flow and optimize cognitive focus for the day ahead."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Practice HRV and Flexibility Training Before Bed",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Use heart-boosting parasympathetic training (as described in Dr. Lagos’ book Heart Breath Mind) to improve HRV and adaptability.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Add a 15-minute heart-boosting breathing session in the evening to support recovery and align emotional regulation with HRV improvements.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Enhance Recovery Capacity",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Alternate active recovery techniques (e.g., stretching or yoga) with passive recovery methods (e.g., sensory decompression in a silent room or light relaxing music) to improve parasympathetic functioning.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Schedule 5-minute recovery breaks during high-stress periods and after mentally demanding tasks or schedule a 15-minute deep work period where you remove yourself from noise and contact with others and let the autonomic nervous system decompress with limited sensory inputs.",
                            "points": []
                        }
                    ]
                },
                {
                    "heading": "Optimize Sleep for Cognitive Recovery",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Integrate targeted supplements and precision temperature control to deepen restorative sleep phases.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Use Lyma Sleep Formula or Huberman's cocktail (magnesium threonate, theanine, apigenin) to promote deeper sleep.",
                                "Invest in an Eight Sleep Mattress for temperature regulation and sleep enhancement.",
                                "Perform pre-sleep resonance breathing to relax the mind and body."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Light Therapy for Circadian Alignment",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Utilize morning light therapy to reset circadian rhythms and improve mental acuity throughout the day. Alternatively, pair a Verilux lamp to your laptop so that you receive increased sources of UV light during the day.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "Spend 10-15 minutes in bright natural light or use light therapy during the day and near your computer.",
                            "points": []
                        }
                    ]
                }
            ],
            "takeaway": "Your metrics highlight the need for a structured approach to improve recovery, HRV, and cognitive adaptability. By implementing a streamlined morning routine, optimizing HRV through parasympathetic training, and focusing on targeted recovery and sleep strategies, you can significantly enhance decision-making, resilience, and overall performance.",
            "next_steps": [
                "🌅 Morning Routine for Resilience",
                "📈 HRV Recovery Training",
                "🛌 Sleep Optimization for Cognitive Efficiency"
            ]
        },
        {
            "protocol_number": "protocol_7",
            "key_insights": [
                {
                    "heading": "HRV Stability",
                    "values": [
                        "Your HRV is in a low range, indicating reduced resilience and recovery capacity.",
                        "Unstable HRV variability suggests your autonomic nervous system is struggling to adapt efficiently to stress, leading to physiological strain.",
                        "This does not necessarily mean that you are not performing in some domains at a high level, but does highlight that you have more energy to harness and you are not performing at your full innate bandwidth.",
                        "Your best {period} HRV score is {max_period_val} ms on {max_period_date}. Our AI recommends you to make note of the behaviours that contributed to this score and try to replicate them in the future."
                    ]
                },
                {
                    "heading": "Heart Rate Patterns",
                    "values": [
                        "A high resting heart rate paired with significant variability (e.g., >10 bpm) suggests heightened sympathetic activation, which may limit recovery and emotional regulation.",
                        "These patterns can compromise focus, decision-making, and long-term recovery."
                    ]
                },
                {
                    "heading": "Performance Indicators",
                    "values": [
                        "Inconsistent energy levels, with noticeable fatigue during prolonged stress.",
                        "Reduced stress resilience, with heightened physiological reactivity to daily demands.",
                        "Limited recovery capacity, making it harder to replenish mental and physical reserves.",
                        "Prone to lapses in emotional and cognitive performance under pressure due to autonomic instability and slower recovery. Myopic thinking can occur when you get in high-stress situations."
                    ]
                }
            ],
            "goals": [
                {
                    "heading": "Morning Routine for Stability and Cognitive Activation",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Design a simple, two-phase routine to prime your system for focus and resilience.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Start with 15 minutes of resonance breathing to reduce overnight autonomic activation and stabilize HRV.",
                                "Follow with 15 minutes of moderate exercise to enhance blood flow and mental clarity.",
                                "End with five resonant breaths to return the body to baseline."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Deepen Recovery Through Optimized Sleep",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Leverage supplementation and temperature regulation to enhance sleep quality and restorative phases.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Use sleep aids such as magnesium threonate, apigenin, or theanine to support deeper sleep.",
                                "Optimize your bedroom environment with an Eight Sleep Mattress for temperature control.",
                                "Add a brief pre-sleep wind-down routine with breathing or calming sounds to prepare your body for restorative sleep."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Regulate Cognitive Stress with Heart Centered Breathing",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Pair focused breathing with intentional emotional amplifiers (e.g., feelings of agility, openness, and calm) to reduce reactivity during high-stress periods.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Before cognitive-demanding tasks, spend 5 minutes on slow breathing while connecting to feelings of calm and confidence in your process.",
                                "Use this approach to prime your system for clarity and decision-making under pressure.",
                                "If you notice your heart rate increase more than 20 beats while sitting still, you are having a cardiovascular response and need to breathe until you bring your heart rate down to within ten beats of your general baseline heart rate."
                            ]
                        }
                    ]
                },
                {
                    "heading": "Address High Variability Days with Dynamic Recovery",
                    "values": [
                        {
                            "heading": "Innovative Strategy",
                            "description": "Implement recovery intervals, alternating movement and relaxation to recalibrate autonomic balance.",
                            "points": []
                        },
                        {
                            "heading": "Action Plan",
                            "description": "",
                            "points": [
                                "Perform 5 minutes of light movement, such as stretching or a walk, followed by 5 resonance breaths following high variability events.",
                                "Use wearable data to track recovery and adjust routines to support greater consistency."
                            ]
                        }
                    ]
                }
            ],
            "takeaway": "Your HRV and heart rate patterns highlight a need for targeted interventions to stabilize your autonomic system and improve resilience. By focusing on structured breathing, optimized recovery, and enhanced sleep, you can significantly boost cognitive performance, stress adaptability, and overall balance.",
            "next_steps": [
                "📈 HRV Stabilization Techniques",
                "🛌 Optimized Sleep for Recovery",
                "💡 Dynamic Recovery Protocols"
            ]
        }
    ];

    useEffect(() => {   
        if(weeklyHrvData.hrv_range_status === "High variability") {
            setAnalysisKeyInsights([...analysis[weeklyHrvData.template_number].key_insights, highHRVMessage]);
        } else {
            setAnalysisKeyInsights(analysis[weeklyHrvData.template_number].key_insights);
        }
    }, []);

    const handleChange = (event) => {
        const selectedVal = event.target.value;
        setSelectedOption(selectedVal);
        setTemplateNumber(Number(selectedVal) - 1);
    }

    const handleSubmit = (action) => {
        submitUpdatedTemplate(originalTemplateNumber, templateNumber, action);
    }

    const applyParameters = (pattern, parameters) => {
        return pattern.replace(/{(.*?)}/g, (_, key) => parameters[key.trim()] || '');
    }

    const renderInsightsText = (protocol_number, heading, value) => {
        const selectedTimePeriod = selectedPeriod === 'weekly-score' ? 'Weekly' : selectedPeriod === 'monthly-score' ? 'Monthly' : 'Quarterly';
        if(protocol_number === "protocol_1" || protocol_number === "protocol_2" || protocol_number === "protocol_4") {
            if(heading === "HRV Stability") {
            return applyParameters(value, {
                val: hrvBreakdownData.metrics.hrv.variability.toFixed(1),
                period: selectedTimePeriod,
                max_period_val: maxPeriodVal,
                max_period_date: formatDateString(maxPeriodDate)
            })
            } else if(heading === "Heart Rate Patterns") {
                return applyParameters(value, {
                    val: hrvBreakdownData.metrics.heart_rate.variability.toFixed(1)
                })
            }
        } else {
            return applyParameters(value, {
                period: selectedTimePeriod,
                max_period_val: maxPeriodVal,
                max_period_date: formatDateString(maxPeriodDate)
            })
        }
        return value;
    }

    const renderModalPopupInsights = () => {
        return (
            <div className="dashboard-carousel">
                <h4 className="recommendation-header">CONCIERGE INSIGHTS</h4>
                <div className="tabs">
                    <button
                        className={activeTab === 0 ? 'active' : ''}
                        onClick={() => setActiveTab(0)}
                    >
                        Analysis of Today's Insights
                    </button>
                    {/* <button
                        className={activeTab === 1 ? 'active' : ''}
                        onClick={() => setActiveTab(1)}
                    >
                        Elite Performance Protocol
                    </button> */}
                </div>
                <div className="tab-content insights-container">
                    {activeTab === 0 &&
                        <>
                        {!showOptimizationProtocols &&
                            <>
                                <div className="flex-container">
                                    <h3>💡 Key Insights</h3>
                                    <button className="elite-performance-btn" onClick={() => setShowOptimizationProtocols(true)}>Elite Performace Protocol</button>
                                </div>
                                <ul className="analysis-ul">
                                    {analysisKeyInsights && analysisKeyInsights.length && analysisKeyInsights.map((insight, index) => (
                                        <li key={`dashboard-analysis-insight-${index}`}>
                                            <h4 className="insight-heading">{insight.heading}</h4>
                                            <ul>
                                                {insight.values.map((value, ind) => (
                                                <li key={`dashboard-analysis-insight-val-${ind}`} >
                                                    <p>
                                                        {index === 2 && 
                                                            <b>{performanceIndicators[ind]}: </b>
                                                        }
                                                        {renderInsightsText(analysis[templateNumber].protocol_number, insight.heading, value)}</p>
                                                </li>
                                                ))}
                                            </ul>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        }
                        {showOptimizationProtocols &&
                            <>
                                <h3>🚀 Optimization Goals</h3>
                                <button className="close-modal-button" onClick={() => setShowOptimizationProtocols(false)}>×</button>
                                <ul>
                                    {analysis[templateNumber].goals.map((goal, index) => (
                                        <li key={`dashboard-goal-${index}`} className="content-container">
                                            <p><b>{goal.heading}</b></p>
                                            <ul>
                                                {goal.values.map((value, ind) => (
                                                    <li key={`dashboard-goal-value-${ind}`}>
                                                        <p><b>{value.heading}:</b> {value.description}</p>
                                                        {value.points.length > 0 &&
                                                            <ul>
                                                                {value.points.map((point, i) => (
                                                                    <li key={`dashboard-goal-point-${i}`}>
                                                                        <p>{point}</p>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        }
                                                    </li>
                                                ))}
                                            </ul>
                                            <hr/>
                                        </li>
                                    ))}
                                </ul>
                                <h3>🎯 Takeaway</h3>
                                <div className="content-container">{analysis[templateNumber].takeaway}<hr/></div>
                                <h3>📋 Next Steps ➡️</h3>
                                <ul className="content-container">
                                    {analysis[templateNumber].next_steps.map((step, index) => (
                                        <li key={`dashboard-next-step-${index}`}>
                                            <p>{step}</p>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        }
                        </>
                    }
                    {/* {activeTab === 1 &&
                        <>
                            <h3>🚀 Optimization Goals</h3>
                            <ul>
                                {analysis[templateNumber].goals.map((goal, index) => (
                                    <li key={`dashboard-goal-${index}`} className="content-container">
                                        <p><b>{goal.heading}</b></p>
                                        <ul>
                                            {goal.values.map((value, ind) => (
                                                <li key={`dashboard-goal-value-${ind}`}>
                                                    <p><b>{value.heading}:</b> {value.description}</p>
                                                    {value.points.length > 0 &&
                                                        <ul>
                                                            {value.points.map((point, i) => (
                                                                <li key={`dashboard-goal-point-${i}`}>
                                                                    <p>{point}</p>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    }
                                                </li>
                                            ))}
                                        </ul>
                                        <hr/>
                                    </li>
                                ))}
                            </ul>
                            <h3>🎯 Takeaway</h3>
                            <div className="content-container">{analysis[templateNumber].takeaway}<hr/></div>
                            <h3>📋 Next Steps ➡️</h3>
                            <ul className="content-container">
                                {analysis[templateNumber].next_steps.map((step, index) => (
                                    <li key={`dashboard-next-step-${index}`}>
                                        <p>{step}</p>
                                    </li>
                                ))}
                            </ul>
                        </>
                    } */}
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="tile">
                <div className="tile-container">
                    <h4 className="page-title">Heart Rate Variability (HRV) & Heart Rate (HR) Metrics</h4>
                    <div className="toggle-button-container today-button-container">
                        <button
                            className="toggle-button"
                            style={{
                                backgroundColor: 'rgb(124 202 204)',
                                color: '#fff',
                            }}
                        >
                            {addOneDayToDateString(maxDateVal)}
                        </button>
                    </div>
                    <div className="horizontal-metrics">
                        <div className="horizontal-metric-item horizontal-metric-heart-item">
                            <div className="heart-metric-container"><div className="metric-value heart-metric">{dailyHrvData.metrics.nocturnal_hrv.toFixed(0)}</div></div>
                            <div className="metric-value-name">Nocturnal HRV</div>
                            <div className="metric-range">{calculateRange(dailyHrvData.metrics.hrv.mean, monthlyHrvData.metrics.hrv, "dashboard")}</div>
                        </div>
                        <div className="horizontal-metric-item horizontal-metric-heart-item">
                            <div className="heart-metric-container"><div className="metric-value heart-metric">{dailyHrvData.metrics.heart_rate.mean.toFixed(0)}</div></div>
                            <div className="metric-value-name">Heart Rate</div>
                            <div className="metric-range">{calculateRange(dailyHrvData.metrics.heart_rate.mean, monthlyHrvData.metrics.heart_rate, "dashboard")}</div>
                        </div>
                        {/* <div className="horizontal-metric-item horizontal-metric-heart-item">
                            <div className="heart-metric-container"><div className="metric-value heart-metric">{dailyHrvData.metrics.breath.mean.toFixed(0)}</div></div>
                            <div className="metric-value-name">Respiration Rate</div>
                            <div className="metric-range">{calculateRange(dailyHrvData.metrics.breath.mean, monthlyHrvData.metrics.breath, "dashboard")}</div>
                        </div> */}
                    </div>
                    <div className="toggle-button-container score-toggle-button-container">
                        <button
                            onClick={() => handleSelect('weekly-score')}
                            className="toggle-button"
                            style={{
                                backgroundColor: selectedPeriod === 'weekly-score' ? 'rgb(124 202 204)' : '#f0f0f0',
                                color: selectedPeriod === 'weekly-score' ? '#fff' : '#000',
                            }}
                        >
                            Weekly
                        </button>

                        <button
                            onClick={() => handleSelect('monthly-score')}
                            className="toggle-button"
                            style={{
                                background: selectedPeriod === 'monthly-score' ? 'rgb(124 202 204)' : '#f0f0f0',
                                color: selectedPeriod === 'monthly-score' ? '#fff' : '#000',
                            }}
                        >
                            Monthly
                        </button>

                        <button
                            onClick={() => handleSelect('quarterly-score')}
                            className="toggle-button"
                            style={{
                                background: selectedPeriod === 'quarterly-score' ? 'rgb(124 202 204)' : '#f0f0f0',
                                color: selectedPeriod === 'quarterly-score' ? '#fff' : '#000',
                            }}
                        >
                            Quarterly
                        </button>
                    </div>
                    <div className="horizontal-metrics">
                        {/* <div className="horizontal-metric-item">
                            <div className="metric-value-name">Average HRV</div>
                            <div className="metric-value">{hrvBreakdownData.metrics.nocturnal_hrv.toFixed(0)}</div>
                        </div> */}
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name">Median HRV</div>
                            <div className="metric-value">{hrvBreakdownData.metrics.hrv.median.toFixed(0)}</div>
                        </div>
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name metric-date">{scorePeriod}-day lowest HRV
                                <div className="date-info" onClick={() => setShowLowestHRVTooltip(true)}>
                                    {showLowestHRVTooltip &&
                                        <span className="date-tooltip">
                                            Your 7-day lowest HRV was on <b>{formatDateString(hrvBreakdownData.metrics.hrv.min.date)}</b>
                                        </span>
                                    }
                                    <i className="fa-solid fa-info date-info-icon"></i>
                                </div>
                            </div>
                            <div className="metric-value">{hrvBreakdownData.metrics.hrv.min.value.toFixed(0)}</div>
                        </div>
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name metric-date">{scorePeriod}-day Highest HRV
                                <div className="date-info" onClick={() => setShowHighestHRVTooltip(true)}>
                                    {showHighestHRVTooltip &&
                                        <span className="date-tooltip">
                                            Your 7-day highest HRV was on <b>{formatDateString(hrvBreakdownData.metrics.hrv.max.date)}</b>
                                        </span>
                                    }
                                    <i className="fa-solid fa-info date-info-icon"></i>
                                </div>
                            </div>
                            <div className="metric-value">{hrvBreakdownData.metrics.hrv.max.value.toFixed(0)}</div>
                        </div>
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name">{scorePeriod}-day HRV Variability</div>
                            <div className="metric-value">{hrvBreakdownData.metrics.hrv.variability.toFixed(1)}</div>
                        </div>
                    </div>
                    <hr className="divider tile-divider" />
                    <div className="horizontal-metrics">
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name">Median HR</div>
                            <div className="metric-value">{hrvBreakdownData.metrics.heart_rate.median.toFixed(0)}</div>
                        </div>
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name metric-date">{scorePeriod}-day lowest HR
                                <div className="date-info" onClick={() => setShowLowestHRTooltip(true)}>
                                    <i className="fa-solid fa-info date-info-icon"></i>
                                    {showLowestHRTooltip &&
                                        <span className="date-tooltip">
                                            Your 7-day lowest HR was on <b>{formatDateString(hrvBreakdownData.metrics.heart_rate.min.date)}</b>
                                        </span>
                                    }
                                </div>
                            </div>
                            <div className="metric-value">{hrvBreakdownData.metrics.heart_rate.min.value.toFixed(0)}</div>
                        </div>
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name metric-date">{scorePeriod}-day Highest HR
                                <div className="date-info" onClick={() => setShowHighestHRTooltip(true)}>
                                    <i className="fa-solid fa-info date-info-icon"></i>
                                    {showHighestHRTooltip &&
                                        <span className="date-tooltip">
                                            Your 7-day highest HR was on <b>{formatDateString(hrvBreakdownData.metrics.heart_rate.max.date)}</b>
                                        </span>
                                    }
                                </div>
                            </div>
                            <div className="metric-value">{hrvBreakdownData.metrics.heart_rate.max.value.toFixed(0)}</div>
                        </div>
                        <div className="horizontal-metric-item">
                            <div className="metric-value-name">{scorePeriod}-day HR Variability</div>
                            <div className="metric-value">{hrvBreakdownData.metrics.heart_rate.variability.toFixed(1)}</div>
                        </div>
                    </div>
                </div>
                <button className="ai-recco-button" onClick={openModal}>Concierge Insights</button>
                {isModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content concierge-insights-modal" ref={modalRef}>
                            <button className="close-modal-button" onClick={closeModal}>×</button>
                            {renderModalPopupInsights()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Dashboard;


